if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.quantityInput = document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
        if (this.quantityInput) {
          this.quantityInput.addEventListener("change", this.onQuantityChange.bind(this));
        }

        // Ensure quantity buttons directly trigger reset (delay to let value update)
        document.addEventListener("click", (e) => {
          if (e.target.closest(".quantity__button")) {
            setTimeout(() => this.onQuantityChange(), 20);
          }
        });

        this.cartData = null;
        this.isVariantSoldOut = this.submitButton.hasAttribute("disabled") && !this.submitButton.classList.contains("added");
        this._lastSellingPlan = null;
        this.fetchCartData();
        this.updateButtonTextWithPrice();

        document.addEventListener('cart:refresh', () => {
          this.fetchCartData();
        });

        this.form.addEventListener("change", () => {
          this.updateButtonState();
        });

        this.form.addEventListener("click", (e) => {
          if (e.target.closest('input[name="selling_plan"]') || e.target.closest('[name="id"]')) {
            setTimeout(() => this.updateButtonState(), 50);
          }
        });

        // Detect Seal Subscriptions widget changes via MutationObserver
        this._observeSellingPlanChanges();

        // Catch clicks on subscription widget radio buttons (outside the form)
        const productInfoContainer = this.closest('product-info') || this.closest('.product__info-wrapper') || this.parentElement;
        if (productInfoContainer) {
          productInfoContainer.addEventListener('click', () => {
            setTimeout(() => this.updateButtonState(), 100);
          });
        }
      }

      onQuantityChange() {
        this.updateButtonState();
        this.updateButtonTextWithPrice();
      }

      _observeSellingPlanChanges() {
        const observer = new MutationObserver(() => {
          const formData = new FormData(this.form);
          const currentSellingPlan = formData.get("selling_plan") || "";
          if (currentSellingPlan !== this._lastSellingPlan) {
            this._lastSellingPlan = currentSellingPlan;
            this.updateButtonState();
          }
        });
        observer.observe(this.form, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'checked', 'disabled'],
        });
      }

      fetchCartData() {
        return fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            this.cartData = cart;
            this.updateButtonState();
          })
          .catch((err) => console.error("Error fetching cart data:", err));
      }

      updateButtonState() {
        if (!this.submitButton || !this.cartData) return;

        const formData = new FormData(this.form);
        const variantId = formData.get("id");
        let sellingPlanId = formData.get("selling_plan");
        const selectedQty = parseInt(formData.get("quantity")) || 1;

        const activeCard = document.querySelector('.purchase-option-card.is-active');
        if (activeCard) {
          if (activeCard.dataset.option === 'subscribe' && !sellingPlanId) {
            const spInput = this.form.querySelector('input[name="selling_plan"]');
            sellingPlanId = spInput?.dataset.defaultPlan || '';
            if (spInput) spInput.value = sellingPlanId;
          } else if (activeCard.dataset.option === 'onetime') {
            sellingPlanId = "";
            const spInput = this.form.querySelector('input[name="selling_plan"]');
            if (spInput) spInput.value = "";
          }
        }

        const existingItem = this.cartData.items.find((item) => {
          const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;

          if (sellingPlanId && sellingPlanId !== "") {
            return item.variant_id == variantId && String(itemSellingPlanId) === String(sellingPlanId);
          } else {
            return item.variant_id == variantId && !itemSellingPlanId;
          }
        });

        if (existingItem && existingItem.quantity === selectedQty) {
          this.submitButton.classList.add("added");
          this.submitButton.setAttribute("disabled", "disabled");
          if (this.submitButtonText) {
            this.submitButtonText.textContent = "Added to cart";
          }
        } else {
          this.submitButton.classList.remove("added");
          if (this.isVariantSoldOut) {
            this.submitButton.setAttribute("disabled", "disabled");
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.soldOut || "Sold out";
            }
          } else {
            this.submitButton.removeAttribute("disabled");
            this.updateButtonTextWithPrice();
          }
        }
      }

      updateButtonTextWithPrice() {
        if (!this.submitButtonText || this.submitButton.classList.contains("added")) return;
        if (this.isVariantSoldOut) {
          this.submitButtonText.textContent = window.variantStrings?.soldOut || "Sold out";
          return;
        }

        const qtyInput = this.form.querySelector('input[name="quantity"]') || document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
        const selectedQty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        const activeCard = document.querySelector('.purchase-option-card.is-active');
        const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');

        let isSubscription = true;
        if (activeCard) {
          isSubscription = activeCard.dataset.option === 'subscribe';
        } else if (sellingPlanInput) {
          isSubscription = Boolean(sellingPlanInput.value && !sellingPlanInput.disabled);
        }

        // Prices (in cents) for the selected pack, kept up to date by the purchase options script in price.liquid
        const purchaseOptions = document.getElementById('purchase-options');
        const priceCents = Number(
          isSubscription ? purchaseOptions?.dataset.subscribePrice : purchaseOptions?.dataset.onetimePrice
        );
        if (!priceCents) {
          this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          return;
        }

        const currency = window.Shopify?.currency?.active || 'USD';
        const totalPrice = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
          (priceCents * selectedQty) / 100
        );

        this.submitButtonText.innerHTML = `Add to cart &middot; ${totalPrice}`;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const spinner = this.submitButton.querySelector(".loading__spinner");

        if (this.submitButtonText) {
          this.submitButtonText.classList.add("hidden");
        }

        if (spinner) {
          spinner.classList.remove("hidden");
        }

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.submitButton.setAttribute("aria-disabled", true);

        this.handleErrorMessage();

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch("/cart.js")
          .then((res) => res.json())
          .then((cart) => {
            const variantId = formData.get("id");
            const newQty = parseInt(formData.get("quantity")) || 1;
            const sellingPlanId = formData.get("selling_plan");

            const existingItem = cart.items.find((item) => {
              const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;

              if (sellingPlanId && sellingPlanId !== "") {
                return item.variant_id == variantId && String(itemSellingPlanId) === String(sellingPlanId);
              } else {
                return item.variant_id == variantId && !itemSellingPlanId;
              }
            });

            if (existingItem) {
              const changeBody = {
                id: existingItem.key,
                quantity: newQty
              };
              if (this.cart) {
                changeBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
                changeBody.sections_url = window.location.pathname;
              }
              return fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(changeBody),
              });
            }

            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.json())
          .then((data) => {
            console.log("ADD SUCCESS", data);

            if (data.status) {
              this.handleErrorMessage(data.description);
              return;
            }

            if (this.submitButtonText) {
              this.submitButtonText.textContent = "Added to cart";
              this.submitButton.classList.add("added");
              this.submitButton.setAttribute("disabled", "disabled");
            }

            let productTitle = "";
            if (data.items) {
              const variantId = formData.get("id");
              const sellingPlanId = formData.get("selling_plan");
              const matchingItem = data.items.find((item) => {
                const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
                if (sellingPlanId && sellingPlanId !== "") {
                  return item.variant_id == variantId && String(itemSellingPlanId) === String(sellingPlanId);
                } else {
                  return item.variant_id == variantId && !itemSellingPlanId;
                }
              });
              productTitle = matchingItem ? matchingItem.title : (document.querySelector("h1")?.textContent?.trim() || "Item");
            } else {
              productTitle = data.title || data.product_title || (document.querySelector("h1")?.textContent?.trim() || "Item");
            }

            this.showToaster(productTitle);

            setTimeout(() => {
              window.location = window.routes.cart_url;
            }, 1000);

            // Rely on cart:refresh to update header count via updateCartCount()
            document.dispatchEvent(new CustomEvent("cart:refresh"));
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            const spinner =
              this.submitButton.querySelector(".loading__spinner");

            if (spinner) spinner.classList.add("hidden");

            if (this.submitButtonText) {
              this.submitButtonText.classList.remove("hidden");
            }

            this.submitButton.removeAttribute("aria-disabled");
          });
      }

      showToaster(productTitle) {
        const existingToaster = document.querySelector(".custom-cart-toaster");
        if (existingToaster) {
          existingToaster.remove();
        }

        if (!document.getElementById("custom-cart-toaster-style")) {
          const style = document.createElement("style");
          style.id = "custom-cart-toaster-style";
          style.textContent = `
            .custom-cart-toaster {
              position: fixed;
              top: 20px;
              right: -350px;
              width: 320px;
              background: rgba(255, 255, 255, 0.95);
              backdrop-filter: blur(10px);
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 8px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
              padding: 16px;
              z-index: 99999;
              display: flex;
              align-items: center;
              gap: 12px;
              transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
              font-family: inherit;
            }
            .custom-cart-toaster.show {
              right: 20px;
            }
            .custom-cart-toaster__icon {
              width: 24px;
              height: 24px;
              color: #10b981;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #ecfdf5;
              border-radius: 50%;
              flex-shrink: 0;
            }
            .custom-cart-toaster__content {
              flex-grow: 1;
            }
            .custom-cart-toaster__title {
              font-size: 14px;
              font-weight: 600;
              color: #111827;
              margin: 0 0 2px 0;
            }
            .custom-cart-toaster__desc {
              font-size: 12px;
              color: #6b7280;
              margin: 0;
            }
          `;
          document.head.appendChild(style);
        }

        const toaster = document.createElement("div");
        toaster.className = "custom-cart-toaster";
        toaster.innerHTML = `
          <div class="custom-cart-toaster__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="custom-cart-toaster__content">
            <p class="custom-cart-toaster__title">Added to cart!</p>
            <p class="custom-cart-toaster__desc">${productTitle}</p>
          </div>
        `;

        document.body.appendChild(toaster);

        // Force reflow
        toaster.offsetHeight;

        // Slide in
        toaster.classList.add("show");
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
          if (text === window.variantStrings?.soldOut || text === window.variantStrings?.unavailable) {
            this.isVariantSoldOut = true;
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          this.isVariantSoldOut = false;
          this.updateButtonTextWithPrice();
        }

        if (disable && !text) {
          return;
        }

        this.updateButtonState();
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
